// Lightweight analytics — fires events to /api/events with session_id cookie + bearer token.
// All sends are fire-and-forget; failures never block the UI.
import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";
const SESSION_COOKIE = "jc3_sid";

function getOrCreateSessionId() {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|;\s*)jc3_sid=([^;]+)/);
  if (m) return decodeURIComponent(m[1]);
  const sid = `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${SESSION_COOKIE}=${encodeURIComponent(sid)}; Max-Age=${oneYear}; Path=/; SameSite=Lax`;
  return sid;
}

export function track(event_type, metadata = {}, page = null) {
  const session_id = getOrCreateSessionId();
  const token = typeof window !== "undefined" ? localStorage.getItem("jc3_token") : null;
  const path = page || (typeof window !== "undefined" ? window.location.pathname : null);
  const body = {
    event_type,
    session_id,
    page: path,
    metadata,
  };
  try {
    fetch(`${API}/api/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* swallow */
  }
  // Mirror to GA4 (gtag) — fire-and-forget, never throw.
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      // GA4 event names must be ≤40 chars, snake_case-friendly. Convert "signup-complete" → "signup_complete".
      const ga_event = String(event_type).replace(/-/g, "_").slice(0, 40);
      if (event_type === "page-view") {
        window.gtag("event", "page_view", {
          page_path: path,
          page_location: typeof window !== "undefined" ? window.location.href : undefined,
          session_id,
          ...metadata,
        });
      } else {
        window.gtag("event", ga_event, {
          page_path: path,
          session_id,
          ...metadata,
        });
      }
    }
  } catch {
    /* swallow */
  }
}

export function useAnalytics() {
  return useCallback((evt, meta, page) => track(evt, meta, page), []);
}

// Page-view tracker — mount once near the router root.
export function usePageViewTracker() {
  const loc = useLocation();
  useEffect(() => {
    track("page-view", { search: loc.search }, loc.pathname);
  }, [loc.pathname, loc.search]);
}
